import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    updateDoc,
    deleteDoc,
    doc,
    arrayUnion,
    arrayRemove,
    onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase.config';
import { getStorage, ref, deleteObject } from 'firebase/storage';

const COLLECTION = 'properties';

/**
 * Normalize a property document read from Firestore.
 * Provides backward compatibility for old docs that have `title` but no `address`.
 * New schema uses `address` as the primary display name; `title` is deprecated.
 */
function normalizeProperty(data) {
    if (!data) return data;
    // If old doc has title but no address, use title as address
    if (!data.address && data.title) {
        data.address = data.title;
    }
    return data;
}

/**
 * Create a new property for a user
 * @param {string} userId - Owner's user ID (auth.uid)
 * @param {Object} propertyData - Property metadata
 * @returns {Promise<string>} - New property document ID
 */
export const createProperty = async (userId, propertyData) => {
    if (!userId) throw new Error('userId is required');

    const propertiesRef = collection(db, COLLECTION);
    const docRef = await addDoc(propertiesRef, {
        userId,
        portfolioId: propertyData.portfolioId || null, // Link to parent portfolio
        address: propertyData.address || '',
        address2: propertyData.address2 || '',
        price: propertyData.price || 0,
        sqft: propertyData.sqft || 0,
        bedrooms: propertyData.bedrooms || 0,
        bathrooms: propertyData.bathrooms || 0,
        description: propertyData.description || '',
        pageUrl: propertyData.pageUrl || '',
        imageUrls: propertyData.imageUrls || [],
        thumbnailUrl: propertyData.thumbnailUrl || '',
        captureFormFields: propertyData.captureFormFields || [
            { id: 'name', label: 'Name', type: 'text', required: true, locked: true },
            { id: 'email', label: 'Email', type: 'email', required: true, locked: true },
            { id: 'phone', label: 'Phone', type: 'tel', required: true, locked: true },
            { id: 'address', label: 'Address', type: 'text', required: true, locked: true },
            { id: 'interestedProperty', label: 'Interested Property', type: 'multiselect', required: true, locked: true },
        ],
        automations: propertyData.automations || {
            addToLeads: true,
        },
        tags: propertyData.tags || [],
        stats: propertyData.stats || {
            totalViews: 0,
            totalLeads: 0,
            lastViewedAt: null,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });

    return docRef.id;
};

/**
 * Fetch all properties for a specific user
 * @param {string} userId - Owner's user ID (auth.uid)
 * @returns {Promise<Array>} - Array of property documents with IDs
 */
export const getUserProperties = async (userId) => {
    if (!userId) throw new Error('userId is required');

    try {
        const propertiesRef = collection(db, COLLECTION);
        const q = query(
            propertiesRef,
            where('userId', '==', userId),
            orderBy('updatedAt', 'desc')
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(docSnap => normalizeProperty({
            id: docSnap.id,
            ...docSnap.data(),
        }));
    } catch (error) {
        console.error('Error fetching user properties:', error);
        throw error;
    }
};

/**
 * Subscribe to real-time updates of user's properties
 * @param {string} userId - Owner's user ID (auth.uid)
 * @param {Function} callback - Called with updated properties array
 * @returns {Function} - Unsubscribe function
 */
export const onPropertiesChange = (userId, callback) => {
    if (!userId) throw new Error('userId is required');

    const propertiesRef = collection(db, COLLECTION);
    const q = query(
        propertiesRef,
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const properties = snapshot.docs.map(docSnap => normalizeProperty({
            id: docSnap.id,
            ...docSnap.data(),
        }));
        callback(properties);
    }, (error) => {
        console.error('Error in properties listener:', error);
    });

    return unsubscribe;
};

/**
 * Fetch a single property by ID
 * @param {string} propertyId - Property document ID
 * @returns {Promise<Object|null>} - Property document data with ID, or null if not found
 */
export const getPropertyById = async (propertyId) => {
    if (!propertyId) throw new Error('propertyId is required');

    try {
        const propertyRef = doc(db, COLLECTION, propertyId);
        const docSnap = await getDoc(propertyRef);

        if (!docSnap.exists()) return null;

        return normalizeProperty({
            id: docSnap.id,
            ...docSnap.data(),
        });
    } catch (error) {
        console.error('Error fetching property:', error);
        throw error;
    }
};

/**
 * Update property fields
 * @param {string} propertyId - Property document ID
 * @param {Object} partialData - Fields to update
 * @returns {Promise<void>}
 */
export const updateProperty = async (propertyId, partialData) => {
    if (!propertyId) throw new Error('propertyId is required');
    if (!partialData || typeof partialData !== 'object') throw new Error('partialData must be a non-empty object');

    try {
        const propertyRef = doc(db, COLLECTION, propertyId);
        await updateDoc(propertyRef, {
            ...partialData,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating property:', error);
        throw error;
    }
};

/**
 * Add image URL to property's imageUrls array
 * @param {string} propertyId - Property document ID
 * @param {string} imageUrl - Full download URL from Firebase Storage
 * @returns {Promise<void>}
 */
export const addImageToProperty = async (propertyId, imageUrl) => {
    if (!propertyId || !imageUrl) throw new Error('propertyId and imageUrl are required');

    try {
        const propertyRef = doc(db, COLLECTION, propertyId);
        await updateDoc(propertyRef, {
            imageUrls: arrayUnion(imageUrl),
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error adding image to property:', error);
        throw error;
    }
};

/**
 * Set thumbnail URL for property (usually first image)
 * @param {string} propertyId - Property document ID
 * @param {string} imageUrl - Full download URL from Firebase Storage
 * @returns {Promise<void>}
 */
export const setPropertyThumbnail = async (propertyId, imageUrl) => {
    if (!propertyId || !imageUrl) throw new Error('propertyId and imageUrl are required');

    try {
        const propertyRef = doc(db, COLLECTION, propertyId);
        await updateDoc(propertyRef, {
            thumbnailUrl: imageUrl,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error setting property thumbnail:', error);
        throw error;
    }
};

/**
 * Remove image URL from property's imageUrls array
 * @param {string} propertyId - Property document ID
 * @param {string} imageUrl - Full download URL to remove
 * @returns {Promise<void>}
 */
export const removeImageFromProperty = async (propertyId, imageUrl) => {
    if (!propertyId || !imageUrl) throw new Error('propertyId and imageUrl are required');

    try {
        const propertyRef = doc(db, COLLECTION, propertyId);
        await updateDoc(propertyRef, {
            imageUrls: arrayRemove(imageUrl),
            updatedAt: serverTimestamp(),
        });

        // If this was the thumbnail, clear it
        const property = await getPropertyById(propertyId);
        if (property?.thumbnailUrl === imageUrl) {
            await updateDoc(propertyRef, {
                thumbnailUrl: property.imageUrls?.[0] || '',
            });
        }
    } catch (error) {
        console.error('Error removing image from property:', error);
        throw error;
    }
};

/**
 * Delete property and all associated images from Storage
 * @param {string} userId - Owner's user ID (for auth verification)
 * @param {string} propertyId - Property document ID
 * @returns {Promise<void>}
 */
export const deleteProperty = async (userId, propertyId) => {
    if (!userId || !propertyId) throw new Error('userId and propertyId are required');

    try {
        // Fetch property to get image URLs
        const property = await getPropertyById(propertyId);
        if (!property) throw new Error('Property not found');

        // Verify ownership
        if (property.userId !== userId) {
            throw new Error('Unauthorized: User does not own this property');
        }

        // Delete all images from Storage
        if (property.imageUrls && Array.isArray(property.imageUrls)) {
            const storage = getStorage();
            for (const imageUrl of property.imageUrls) {
                try {
                    // Extract storage path from download URL
                    const storagePath = decodeURIComponent(imageUrl.split('/o/')[1].split('?')[0]);
                    const imageRef = ref(storage, storagePath);
                    await deleteObject(imageRef);
                } catch (e) {
                    console.warn(`Failed to delete image from Storage: ${e.message}`);
                }
            }
        }

        // Delete property document from Firestore
        const propertyRef = doc(db, COLLECTION, propertyId);
        await deleteDoc(propertyRef);
    } catch (error) {
        console.error('Error deleting property:', error);
        throw error;
    }
};

// ============================================================
// PROPERTY RESPONSES (Form Submissions)
// ============================================================

const RESPONSES_COLLECTION = 'propertyResponses';

/**
 * Get all responses for a specific property
 * @param {string} propertyId - Property document ID
 * @returns {Promise<Array>} - Array of response documents
 */
export const getPropertyResponses = async (propertyId) => {
    if (!propertyId) throw new Error('propertyId is required');

    try {
        const responsesRef = collection(db, RESPONSES_COLLECTION);
        const q = query(
            responsesRef,
            where('propertyId', '==', propertyId),
            orderBy('submittedAt', 'desc')
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
    } catch (error) {
        console.error('Error fetching property responses:', error);
        throw error;
    }
};

/**
 * Subscribe to real-time updates of property responses
 * @param {string} propertyId - Property document ID
 * @param {Function} callback - Called with updated responses array
 * @returns {Function} - Unsubscribe function
 */
export const onPropertyResponsesChange = (propertyId, callback) => {
    if (!propertyId) {
        callback([]);
        return () => { };
    }

    const responsesRef = collection(db, RESPONSES_COLLECTION);
    const q = query(
        responsesRef,
        where('propertyId', '==', propertyId),
        orderBy('submittedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const responses = snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
        callback(responses);
    }, (error) => {
        console.error('Error in property responses listener:', error);
        callback([]);
    });

    return unsubscribe;
};

/**
 * Add a new response to a property (typically called from public form)
 * @param {string} propertyId - Property document ID
 * @param {Object} responseData - Form submission data
 * @returns {Promise<string>} - New response document ID
 */
export const addPropertyResponse = async (propertyId, responseData) => {
    if (!propertyId) throw new Error('propertyId is required');

    const responsesRef = collection(db, RESPONSES_COLLECTION);
    const docRef = await addDoc(responsesRef, {
        propertyId,
        ...responseData,
        status: 'new',
        submittedAt: serverTimestamp(),
    });

    // Update property stats
    const property = await getPropertyById(propertyId);
    if (property) {
        await updateProperty(propertyId, {
            stats: {
                ...property.stats,
                totalLeads: (property.stats?.totalLeads || 0) + 1,
            },
        });
    }

    return docRef.id;
};
